import { useEffect, useRef, useState } from 'react'
import { cabinetApi, type VesselSearchHit } from '../api'
import { useT } from '../i18n'
import { flagLabel } from './flagFilter'
import { vesselLabel } from './vesselLabel'

/**
 * Строка поиска судна над кнопками флота.
 *
 * Найти судно глазами среди тысяч точек нельзя (фидбек владельца 2026-08-07).
 * Ввод дебаунсится: запрос сканирует таблицу судов (~110 мс на проде), и посылать
 * его на каждую букву незачем.
 */

/** Пауза после последней буквы, мс. */
const DEBOUNCE_MS = 300

export function VesselSearch({ onPick }: { onPick: (v: VesselSearchHit) => void }) {
  const t = useT()
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<VesselSearchHit[]>([])
  const [busy, setBusy] = useState(false)
  const [searched, setSearched] = useState(false)
  // Гонка ответов: медленный ответ на «ВОС» не должен перетереть быстрый на
  // «ВОСТОК» — считаем актуальным только последний запрос.
  const seqRef = useRef(0)

  useEffect(() => {
    const text = q.trim()
    if (text.length < 2) {
      setHits([])
      setSearched(false)
      return
    }
    const seq = ++seqRef.current
    setBusy(true)
    const id = setTimeout(() => {
      cabinetApi
        .vesselSearch(text)
        .then(r => {
          if (seq !== seqRef.current) return
          setHits(r.vessels)
          setSearched(true)
        })
        .catch(() => {
          if (seq !== seqRef.current) return
          // Молчать нельзя: пустой список читался бы как «судна нет».
          setHits([])
          setSearched(true)
        })
        .finally(() => {
          if (seq === seqRef.current) setBusy(false)
        })
    }, DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [q])

  const pick = (v: VesselSearchHit) => {
    onPick(v)
    setQ('')
    setHits([])
    setSearched(false)
  }

  return (
    <div className="relative">
      {/* Никакой автоправки текста. Ищут здесь ИМЕНА СОБСТВЕННЫЕ и цифры:
          «ВОСТОК-7», «Kapitan Malakhov», MMSI 273896570. Словарь телефона таких
          слов не знает и правит их на похожие, автозаглавная буква ломает
          латиницу в середине, а подстановка браузера предлагает прошлые запросы
          поверх списка судов. Правка приходит ПОСЛЕ дебаунса — человек видит
          результат по одному слову, а запрос уходит по другому. */}
      <input
        type="search"
        value={q}
        onChange={e => setQ(e.target.value)}
        autoCorrect="off"
        autoCapitalize="off"
        autoComplete="off"
        spellCheck={false}
        placeholder={t('models.vesselSearch.placeholder')}
        aria-label={t('models.vesselSearch.aria')}
        className="w-full rounded-lg border border-hull bg-trench px-2.5 py-1.5 text-xs text-foam outline-none placeholder:text-faint focus:border-sonar"
      />
      {q.trim().length >= 2 && (
        <div className="mt-1 max-h-56 overflow-y-auto rounded-lg border border-hull bg-deep">
          {busy && <div className="px-2.5 py-1.5 text-[11px] text-faint">{t('common.searching')}</div>}
          {!busy && searched && hits.length === 0 && (
            <div className="px-2.5 py-1.5 text-[11px] text-faint">
              {t('models.vesselSearch.nothing')}
            </div>
          )}
          {hits.map(v => {
            const label = vesselLabel(v)
            return (
              <button
                key={v.mmsi}
                type="button"
                onClick={() => pick(v)}
                className="flex w-full items-baseline justify-between gap-2 px-2.5 py-1.5 text-left text-xs text-foam transition-colors hover:bg-haze"
              >
                <span className="truncate">
                  {label.primary}
                  {/* Латиница нужна рядом: по ней судно ищут в чужих системах. */}
                  {label.secondary && (
                    <span className="ml-1.5 text-[10px] text-faint">{label.secondary}</span>
                  )}
                </span>
                <span className="tnum shrink-0 text-[10px] text-faint">
                  {v.flag_state ? flagLabel(v.flag_state) : '—'} · {v.mmsi}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
